/**
 * Single source of truth for the `user_feed_element_events` vocabulary on the
 * frontend — the wire contract behind POST /api/stories/{id}/events.
 *
 * The backend owns this taxonomy in `backend/app/queries/user_event_types.py`
 * (`STATUS_EVENT_PAIRS`, `INTERACTION_EVENT_TYPES`, `VALID_EVENT_TYPES`);
 * this module is the frontend half of the same contract, pinned to the
 * backend owner by `backend/tests/test_mirror_parity.py`. The strings used to
 * be re-typed as bare literals across eight files — `recordEvent`'s parameter
 * was a plain `string`, so a typo compiled fine and the server silently
 * rejected it — MyActivityPage carried a third full copy of the vocabulary as
 * its label map, and each toggle site re-derived by hand which event flips
 * which story flag (read/unread -> is_read, ...). Route every event spelling
 * through this module so the vocabulary lives in exactly one place per stack,
 * with the parity test tying the two stacks together.
 */

// Status events come in [positive, negative] pairs and represent toggleable
// state; the key is the category, the first member means "this status is on".
export const STATUS_EVENT_PAIRS = {
  read: ['read', 'unread'],
  flag: ['flag', 'unflag'],
} as const;

// Fire-and-forget analytics signals with no inverse and no status semantics.
export const INTERACTION_EVENT_TYPES = [
  'click_external',
  'expand',
  'show_summary',
  'expand_topic',
  'return_to_feed',
] as const;

export type StatusCategory = keyof typeof STATUS_EVENT_PAIRS;
export type StatusEventType = (typeof STATUS_EVENT_PAIRS)[StatusCategory][number];
export type InteractionEventType = (typeof INTERACTION_EVENT_TYPES)[number];
export type UserEventType = StatusEventType | InteractionEventType;

/** The event that turns a status on (`true`) or off (`false`). */
export function statusEvent(category: StatusCategory, on: boolean): StatusEventType {
  const [positive, negative] = STATUS_EVENT_PAIRS[category];
  return on ? positive : negative;
}

// The decorated-story flag each status category toggles (the shape
// decorate_story_rows / apply_default_user_status attach server-side).
const STATUS_FLAG_FIELDS = {
  read: 'is_read',
  flag: 'is_flagged',
} as const;

export interface StatusFlags {
  is_read: boolean;
  is_flagged: boolean;
}

/**
 * The story-flag change a just-recorded event implies, e.g. `'unflag'` ->
 * `{ is_flagged: false }`. Interaction events return null: they carry no
 * status semantics. The frontend counterpart of the backend's
 * `category_for` / `is_positive` pair.
 */
export function statusFlagUpdate(eventType: UserEventType): Partial<StatusFlags> | null {
  for (const category of Object.keys(STATUS_EVENT_PAIRS) as StatusCategory[]) {
    const [positive, negative] = STATUS_EVENT_PAIRS[category];
    if (eventType === positive || eventType === negative) {
      return { [STATUS_FLAG_FIELDS[category]]: eventType === positive };
    }
  }
  return null;
}

// Display names for the activity log. Keyed by the full union so adding an
// event type without naming it is a compile error.
export const EVENT_LABELS: Record<UserEventType, string> = {
  read: 'Read',
  unread: 'Unread',
  flag: 'Bookmarked',
  unflag: 'Unbookmarked',
  click_external: 'Link out',
  expand: 'Expand',
  show_summary: 'Show summary',
  expand_topic: 'Expand topic',
  return_to_feed: 'Back to feed',
};

/**
 * Label for a stored event row. Stored rows are `string`, not the union:
 * the table can hold rows recorded under retired vocabularies, which fall
 * back to their raw spelling.
 */
export function eventLabel(eventType: string): string {
  return (EVENT_LABELS as Record<string, string>)[eventType] ?? eventType;
}
